import type {
  CSSProperties,
  HTMLAttributes,
  ReactNode,
} from "react";

import { useInView } from "./useInView";
import { usePrefersReducedMotion } from "./usePrefersReducedMotion";
import "./motion.css";

export type RevealVariant =
  | "fade"
  | "up"
  | "down"
  | "left"
  | "right"
  | "scale";

type RevealElement =
  | "div"
  | "section"
  | "article"
  | "aside"
  | "header"
  | "footer"
  | "li"
  | "span";

interface MotionProperties extends CSSProperties {
  "--motion-delay"?: string;
  "--motion-distance"?: string;
  "--motion-duration"?: string;
}

export interface RevealProps extends Omit<
  HTMLAttributes<HTMLElement>,
  "children"
> {
  children: ReactNode;
  /** The semantic element to render. */
  as?: RevealElement;
  /** Base delay in milliseconds. */
  delay?: number;
  /** Transition duration in milliseconds. */
  duration?: number;
  /** Travel distance in pixels for directional variants. */
  distance?: number;
  /** Direction/shape of the entrance. */
  variant?: RevealVariant;
  /** Reveal only once, or replay when the element leaves and re-enters. */
  once?: boolean;
  /** Visible proportion required before entering. */
  threshold?: number;
  /** IntersectionObserver root margin. */
  rootMargin?: string;
  /** Position within a staggered set, starting at zero. */
  order?: number;
  /** Milliseconds added per `order` step. */
  stagger?: number;
}

/**
 * A semantic, viewport-triggered entrance wrapper. Hidden content is never
 * removed from the accessibility tree, and reduced-motion users see it
 * immediately without transforms or delay.
 *
 * @example
 * <Reveal as="article" order={index} stagger={80} variant="up">
 *   <FeatureCard />
 * </Reveal>
 */
export function Reveal({
  as: Component = "div",
  children,
  className,
  delay = 0,
  distance = 22,
  duration = 620,
  once = true,
  order = 0,
  rootMargin,
  stagger = 0,
  style,
  threshold,
  variant = "up",
  ...rest
}: RevealProps) {
  const prefersReducedMotion = usePrefersReducedMotion();
  const { inView, ref } = useInView<HTMLElement>({
    disabled: prefersReducedMotion,
    once,
    rootMargin,
    threshold,
  });
  const resolvedDelay = Math.max(0, delay + order * stagger);
  const motionStyle: MotionProperties = {
    "--motion-delay": `${resolvedDelay}ms`,
    "--motion-distance": `${Math.max(0, distance)}px`,
    "--motion-duration": `${Math.max(0, duration)}ms`,
    ...style,
  };
  const classes = ["motion-reveal", `motion-reveal--${variant}`, className]
    .filter(Boolean)
    .join(" ");

  return (
    <Component
      {...rest}
      ref={ref}
      className={classes}
      data-motion-state={inView ? "visible" : "hidden"}
      style={motionStyle}
    >
      {children}
    </Component>
  );
}
